"use client";

import { GoogleLogin } from "@react-oauth/google";
import {
  Button,
  Card,
  Divider,
  Input,
  Text,
} from "@treebo-noss/superhero-design-system";
import Link from "next/link";
import { useState } from "react";
import { LoginMethodToggle } from "@/features/login/components/LoginMethodToggle";
import {
  useGoogleAuth,
  useLoginForm,
  useOtpFlow,
} from "@/features/login/hooks";
import { BookingNotFoundModal } from "./BookingNotFoundModal";
import { OTPVerificationModal } from "./OTPVerificationModal";

interface LoginCardProps {
  isBookingNotFound?: boolean;
}

export const LoginCard = ({ isBookingNotFound = false }: LoginCardProps) => {
  const [isBookingNotFoundOpen, setIsBookingNotFoundOpen] =
    useState(isBookingNotFound);
  const [bookingNotFoundContact, setBookingNotFoundContact] = useState<
    string | undefined
  >(undefined);

  const handleBookingNotFound = (contactInfo?: string) => {
    setBookingNotFoundContact(contactInfo);
    setIsBookingNotFoundOpen(true);
  };

  const closeBookingNotFoundModal = () => setIsBookingNotFoundOpen(false);

  const {
    loginMethod,
    phoneNumber,
    setPhoneNumber,
    email,
    setEmail,
    setHasBlurred,
    isInputValid,
    showError,
    getInputStatus,
    toggleLoginMethod,
    switchToEmail,
  } = useLoginForm();

  const {
    showOTPModal,
    otpError,
    showEmailFallback,
    isSendingOtp,
    isVerifyingOtp,
    isRedirecting,
    handleContinue,
    handleVerifyOTP,
    handleResendOTP,
    closeOTPModal,
  } = useOtpFlow({
    loginMethod,
    phoneNumber,
    email,
    onBookingNotFound: () =>
      handleBookingNotFound(loginMethod === "phone" ? phoneNumber : email),
  });

  const { handleGoogleSuccess } = useGoogleAuth({
    onBookingNotFound: () => handleBookingNotFound(),
  });

  const handleUseEmail = () => {
    closeOTPModal();
    switchToEmail();
  };

  return (
    <Card radius="lg" className="bg-white shadow-bottom-lg p-4">
      <div className="flex flex-col">
        <Text
          size="s"
          as="p"
          weight="semibold"
          className="text-neutral-700 mb-3"
        >
          Login to Guest Connect
        </Text>

        {loginMethod === "phone" ? (
          <Input
            type="tel"
            size="large"
            value={phoneNumber}
            onChange={(e) =>
              setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
            }
            onBlur={() => setHasBlurred(true)}
            placeholder="Enter mobile number"
            inputMode="numeric"
            status={getInputStatus()}
            helpText={
              showError ? "Invalid entry, please try again." : undefined
            }
            preTab={
              <Text size="s" className="text-neutral-500">
                +91
              </Text>
            }
          />
        ) : (
          <Input
            size="large"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setHasBlurred(true)}
            placeholder="Enter email address"
            status={getInputStatus()}
            helpText={
              showError ? "Invalid entry, please try again." : undefined
            }
          />
        )}

        <div className="flex items-center justify-between mt-4 mb-3">
          <Text size="xs" className="text-neutral-600">
            We will send you an OTP to login
          </Text>
          <LoginMethodToggle
            loginMethod={loginMethod}
            onToggle={toggleLoginMethod}
          />
        </div>

        <Button
          variant="filled"
          className="w-full mb-4"
          disabled={!isInputValid}
          loading={isSendingOtp}
          onClick={handleContinue}
        >
          <Text size="s" weight="medium">
            {isSendingOtp ? "SENDING OTP..." : "CONTINUE"}
          </Text>
        </Button>

        <Divider className="mb-4">OR</Divider>

        <div className="flex flex-col items-center gap-1 mb-4">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() =>
              console.error("Google sign-in was cancelled or failed.")
            }
            type="icon"
            shape="circle"
            size="large"
          />
          <Text size="s" className="text-neutral-600">
            Sign in with Google
          </Text>
        </div>

        <Text size="xs" className="text-neutral-700">
          By proceeding, you agree to our{" "}
          <Link href="/terms" className="text-primary-900">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href="/privacy" className="text-primary-900">
            Privacy Policy
          </Link>
          .
        </Text>
      </div>

      <BookingNotFoundModal
        isOpen={isBookingNotFoundOpen}
        onClose={closeBookingNotFoundModal}
        contactInfo={bookingNotFoundContact}
      />

      <OTPVerificationModal
        open={showOTPModal}
        onClose={closeOTPModal}
        phoneOrEmail={loginMethod === "phone" ? phoneNumber : email}
        loginMethod={loginMethod}
        onVerify={handleVerifyOTP}
        onResendOTP={handleResendOTP}
        onChangeInput={closeOTPModal}
        onUseEmail={handleUseEmail}
        isError={otpError}
        showEmailFallback={showEmailFallback}
        isLoading={isVerifyingOtp || isRedirecting}
      />
    </Card>
  );
};
